import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { safeRedirectPath, SSO_RETURN_TO_KEY } from '@/lib/safeRedirect';
import { api } from '@/lib/api/endpoints';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Database, Eye, EyeOff, AlertCircle, Loader2, Building2 } from 'lucide-react';

const SSO_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Microsoft sign-in was cancelled. Please try again.',
  domain_not_allowed: "Your Microsoft account's domain is not permitted for sign-in.",
  user_not_found:
    'No Lakehouse account is linked to that Microsoft identity. Contact an administrator.',
  invalid_state: 'Microsoft sign-in failed. Please try again.',
  invalid_token: 'Microsoft sign-in failed. Please try again.',
  provider_error: 'Microsoft sign-in failed. Please try again.',
  rate_limited: 'Too many sign-in attempts. Please wait a few minutes and try again.',
  server_error: 'Sign-in is temporarily unavailable. Please try again shortly.',
};

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { login, isLoading } = useAuthStore();

  // The route the operator was bounced from (preserved by ProtectedRoute, #1079),
  // reused for both the password redirect and the SSO stash (#1124).
  const from = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)
    ?.from;

  // Where to send the operator after a successful password login: the route
  // they were bounced from, else /dashboard (#1079).
  const redirectTo = safeRedirectPath(from);

  // Fetch SSO config on mount
  useEffect(() => {
    api.auth
      .ssoConfig()
      .then((r: { data?: { microsoft?: { enabled?: boolean } } }) => setSsoEnabled(r.data?.microsoft?.enabled ?? false))
      .catch(() => setSsoEnabled(false));
  }, []);

  // Map sso_error query param to a user-friendly message
  const ssoErrorCode = searchParams.get('sso_error');
  const ssoErrorMessage = ssoErrorCode
    ? (SSO_ERROR_MESSAGES[ssoErrorCode] ?? 'Microsoft sign-in failed. Please try again.')
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await login(email, password);
      // TODO: Implement remember me functionality
      if (rememberMe) {
        localStorage.setItem('remember_me', 'true');
      }
      navigate(redirectTo);
    } catch (err: any) {
      // Enhanced error messages based on error type
      if (err.status === 401) {
        setError('Invalid email or password. Please try again.');
      } else if (err.status === 422) {
        setError('Please enter a valid email address and password.');
      } else if (err.status === 429) {
        setError('Too many login attempts. Please try again in 5 minutes.');
      } else if (err.status === 503) {
        setError('Authentication is temporarily unavailable. Please try again shortly.');
      } else if (err.status === 500) {
        setError('Server error. Please contact support if this persists.');
      } else {
        setError(err.message || 'Login failed. Please check your credentials.');
      }
      // Focus back to email field for retry
      document.getElementById('email')?.focus();
    }
  };

  const handleMicrosoftSignIn = () => {
    // Full-page nav discards router state, so carry the attempted route across
    // the SSO round-trip in sessionStorage; RootLayout consumes it on landing (#1124).
    try {
      if (from?.pathname) {
        sessionStorage.setItem(
          SSO_RETURN_TO_KEY,
          JSON.stringify({ pathname: from.pathname, search: from.search ?? '', hash: from.hash ?? '' }),
        );
      } else {
        // No attempted route on this click — clear any stale stash from an earlier
        // bounce so a plain (non-bounce) SSO login isn't misdirected there (#1124).
        sessionStorage.removeItem(SSO_RETURN_TO_KEY);
      }
    } catch {
      // sessionStorage unavailable (private mode/quota) — proceed without stashing.
    }
    window.location.assign('/api/v1/auth/sso/microsoft/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="flex flex-col lg:flex-row w-full max-w-6xl gap-8">
        {/* Left side: Branding/illustration (hidden on mobile) */}
        <div className="hidden lg:flex lg:flex-1 items-center justify-center">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <Database className="h-24 w-24 text-primary" />
            </div>
            <h2 className="text-4xl font-bold tracking-tight">Welcome to Lakehouse</h2>
            <p className="text-muted-foreground text-lg max-w-md">
              Enterprise data platform for modern analytics and data management
            </p>
          </div>
        </div>

        {/* Right side: Login form */}
        <Card className="w-full lg:flex-1 lg:max-w-md">
          <CardHeader className="space-y-1 p-4 sm:p-6">
            <div className="flex items-center justify-center mb-2 lg:hidden">
              <Database className="h-12 w-12 text-primary" />
            </div>
            <CardTitle className="text-2xl sm:text-3xl font-bold tracking-tight text-center lg:text-left">
              Lakehouse Admin
            </CardTitle>
            <CardDescription className="text-sm sm:text-base text-center lg:text-left">
              Sign in to your account to access the data lakehouse
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-3">
                <Label htmlFor="email">
                  Email <span className="text-destructive" aria-label="required">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  aria-required="true"
                  autoFocus
                  autoComplete="email"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">
                    Password <span className="text-destructive" aria-label="required">*</span>
                  </Label>
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 text-xs h-auto"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Forgot password?
                  </Button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    aria-required="true"
                    autoComplete="current-password"
                    disabled={isLoading}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked === true)}
                />
                <Label
                  htmlFor="remember"
                  className="text-sm font-normal cursor-pointer"
                >
                  Remember me
                </Label>
              </div>

              {(error || ssoErrorMessage) && (
                <Alert variant="destructive" role="alert" aria-live="polite">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error || ssoErrorMessage}</AlertDescription>
                </Alert>
              )}

              {/* Static by design: login failures are deliberately generic
                  (#1260), so this guidance is shown unconditionally. Rendering
                  it in response to a specific failure reason would rebuild the
                  account-enumeration oracle in the client. */}
              <p
                data-testid="login-help"
                className="text-sm text-muted-foreground"
              >
                Trouble signing in? If you registered recently, check your email for a
                verification link. If your account was deactivated, contact support.
              </p>

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || !email || !password}
              >
                {isLoading && (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    <span className="sr-only">Loading, please wait...</span>
                  </>
                )}
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            {ssoEnabled && (
              <>
                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                {/* Microsoft SSO button — full-page browser nav, not XHR */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleMicrosoftSignIn}
                >
                  <Building2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  Sign in with Microsoft
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
